import { useMemo, useState } from 'react';
import { Modal } from '@mantine/core';
import { Button } from '@/components/Button';
import { Card, CardHead } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Status } from '@/components/Status';
import {
  useCreateUser,
  useResetUserPassword,
  useSetUserDisabled,
  useUpdateUser,
  useUsers,
} from '@/hooks/useUsers';
import { ROLE_LABELS, type PlatformRole, type User } from '@/api/users';
import { useAuth } from '@/store/auth';
import { formatDateTime } from '@/lib/format';

const ALL_ROLES: PlatformRole[] = ['ADMIN', 'OPERATOR', 'VIEWER', 'HEATMAP'];

const ROLE_TABS: [PlatformRole | '', string][] = [
  ['', 'Tous'],
  ['ADMIN', 'Administrateurs'],
  ['OPERATOR', 'Opérateurs'],
  ['VIEWER', 'Lecteurs'],
  ['HEATMAP', 'Accès carte'],
];

/** ADMIN ouvre la carte d'office : compter les deux, sinon le sous-titre ment. */
function opensMap(u: User): boolean {
  const roles = u.roles ?? [];
  return roles.includes('HEATMAP') || roles.includes('ADMIN');
}

/**
 * Comptes de la plateforme.
 *
 * Une seule base d'utilisateurs pour tout : le back-office ET la carte. Ce
 * sont les RÔLES qui ouvrent les portes — ADMIN / OPERATOR / VIEWER pour le
 * back-office, « Accès heatmap » pour la carte. Un compte qui ne porte que
 * l'accès heatmap ne voit aucune fiche : le serveur le lui refuse.
 *
 * Les comptes fédérés (Crystal…) apparaissent ici à leur première connexion ;
 * on peut leur donner des rôles et couper leur accès, mais pas toucher à leur
 * mot de passe, qui vit chez leur système d'origine.
 */
export function UsersPage() {
  const me = useAuth((s) => s.user);
  const [q, setQ] = useState('');
  const [role, setRole] = useState<PlatformRole | ''>('');
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [secret, setSecret] = useState<{ user: User; password: string } | null>(null);

  const list = useUsers({ q: q || undefined, role: role || undefined, includeDisabled, limit: 200 });
  const users = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const mapCount = useMemo(() => users.filter((u) => opensMap(u) && !u.disabled).length, [users]);

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <div className="pt">Comptes</div>
          <div className="ps">
            Une seule base d'utilisateurs pour le back-office et la carte : ce sont les rôles qui
            ouvrent les portes.
          </div>
        </div>
        <div className="ph-right">
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            Nouveau compte
          </Button>
        </div>
      </div>

      <div className="toolbar">
        <label className="input grow">
          <Icon name="search" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un email, un nom…"
          />
        </label>
        <div className="seg">
          {ROLE_TABS.map(([value, label]) => (
            <button
              key={value || 'all-roles'}
              type="button"
              className={role === value ? 'on' : ''}
              onClick={() => setRole(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="once-toggle">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          />
          Comptes désactivés
        </label>
      </div>

      <Card>
        <CardHead
          title={`${total} compte${total > 1 ? 's' : ''}`}
          subtitle={`${mapCount} avec accès à la carte`}
        />
        {users.length === 0 ? (
          <EmptyState
            icon="users"
            title="Aucun compte"
            subtitle={
              list.isLoading ? 'Chargement…' : 'Aucun compte ne correspond à cette recherche.'
            }
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Compte</th>
                <th>Source</th>
                <th>Rôles</th>
                <th>Dernière connexion</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={u.disabled ? { opacity: 0.55 } : undefined}>
                  <td>
                    <div className="cell-stack">
                      <span className="pri">{u.displayName || u.email || u.externalId}</span>
                      <span className="ph">{u.email || u.externalId}</span>
                    </div>
                  </td>
                  <td>
                    <Status kind={u.source === 'LOCAL' ? 'mute' : 'ok'}>{u.source}</Status>
                  </td>
                  <td>
                    <div className="role-chips">
                      {(u.roles ?? []).length === 0 && <span className="muted">aucun</span>}
                      {(u.roles ?? []).map((r) => (
                        <span
                          key={r}
                          className={r === 'HEATMAP' ? 'role-chip accent' : 'role-chip'}
                          title={ROLE_LABELS[r as PlatformRole]?.help}
                        >
                          {ROLE_LABELS[r as PlatformRole]?.label ?? r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {u.disabled ? (
                      <Status kind="warn">Désactivé</Status>
                    ) : u.lastLoginAt ? (
                      formatDateTime(u.lastLoginAt)
                    ) : (
                      <span className="muted">jamais</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(u)}>
                      Gérer
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <CreateUserModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(user, password) => {
          setCreateOpen(false);
          if (password) setSecret({ user, password });
        }}
      />

      {editing && (
        <ManageUserModal
          user={editing}
          isSelf={editing.id === me?.accountId}
          onClose={() => setEditing(null)}
          onPassword={(user, password) => {
            setEditing(null);
            setSecret({ user, password });
          }}
        />
      )}

      {secret && (
        <PasswordOnceModal
          user={secret.user}
          password={secret.password}
          onClose={() => setSecret(null)}
        />
      )}
    </div>
  );
}

/**
 * Cases à cocher des rôles, avec leur effet en clair.
 *
 * Hors d'un `.field` : la feuille de style y donne `width:100%` à tout
 * `input`, ce qui étire une case à cocher sur toute la ligne.
 */
function RolePicker({
  value,
  onChange,
}: {
  value: PlatformRole[];
  onChange: (roles: PlatformRole[]) => void;
}) {
  return (
    <div className="role-pick">
      {ALL_ROLES.map((r) => (
        <label key={r} className="role-pick-row">
          <input
            type="checkbox"
            checked={value.includes(r)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))
            }
          />
          <span>
            <span className="rp-name">{ROLE_LABELS[r].label}</span>
            <span className="rp-help">{ROLE_LABELS[r].help}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

function CreateUserModal({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (user: User, password?: string) => void;
}) {
  const create = useCreateUser();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<PlatformRole[]>(['HEATMAP']);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setEmail('');
    setDisplayName('');
    setRoles(['HEATMAP']);
    setError(null);
    create.reset();
    onClose();
  }

  return (
    <Modal opened={opened} onClose={close} title="Nouveau compte" size={520} centered>
      <div className="modal-form">
        <div className="field">
          <label>
            Email <span className="req">requis</span>
          </label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@mafa.ci"
            autoFocus
          />
          <div className="hint">Sert d'identifiant de connexion.</div>
        </div>

        <div className="field">
          <label>Nom affiché</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Facultatif — l'email par défaut"
          />
        </div>

        <div>
          <div className="form-label">Rôles</div>
          <RolePicker value={roles} onChange={setRoles} />
        </div>

        <div className="note">
          Un mot de passe est généré et affiché <strong>une seule fois</strong> après la création :
          rien ne permet de le relire ensuite, seulement d'en générer un nouveau.
        </div>

        {error && <div className="err-msg">{error}</div>}

        <div className="modal-actions">
          <Button variant="ghost" onClick={close} disabled={create.isPending}>
            Annuler
          </Button>
          <Button
            variant="primary"
            icon="user"
            loading={create.isPending}
            disabled={!email.includes('@') || roles.length === 0}
            onClick={() => {
              setError(null);
              create.mutate(
                { email: email.trim(), displayName: displayName.trim() || undefined, roles },
                {
                  onSuccess: (res) => {
                    onCreated(res.user, res.password);
                    close();
                  },
                  onError: (e: Error) => setError(e.message),
                },
              );
            }}
          >
            Créer le compte
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ManageUserModal({
  user,
  isSelf,
  onClose,
  onPassword,
}: {
  user: User;
  isSelf: boolean;
  onClose: () => void;
  onPassword: (user: User, password: string) => void;
}) {
  const update = useUpdateUser();
  const reset = useResetUserPassword();
  const status = useSetUserDisabled();
  const [roles, setRoles] = useState<PlatformRole[]>((user.roles ?? []) as PlatformRole[]);
  const [error, setError] = useState<string | null>(null);
  const isLocal = user.source === 'LOCAL';

  return (
    <Modal
      opened
      onClose={onClose}
      title={user.displayName || user.email || user.externalId}
      size={520}
      centered
    >
      <div className="modal-form">
        <div className="modal-sub">
          {user.email || user.externalId} · {user.source}
          {user.disabled ? ' · désactivé' : ''}
        </div>

        <section className="modal-section">
          <div className="form-label">Rôles</div>
          <RolePicker value={roles} onChange={setRoles} />
          <div className="modal-actions tight">
            <Button
              variant="primary"
              icon="check"
              loading={update.isPending}
              onClick={() => {
                setError(null);
                update.mutate(
                  { id: user.id, roles },
                  { onSuccess: onClose, onError: (e: Error) => setError(e.message) },
                );
              }}
            >
              Enregistrer les rôles
            </Button>
          </div>
        </section>

        <section className="modal-section">
          <div className="form-label">Mot de passe</div>
          {isLocal ? (
            <>
              <div className="hint">
                Un nouveau mot de passe est généré et affiché une seule fois. L'ancien cesse
                aussitôt de fonctionner.
              </div>
              <div className="modal-actions tight">
                <Button
                  icon="key"
                  loading={reset.isPending}
                  onClick={() => {
                    setError(null);
                    reset.mutate(
                      { id: user.id },
                      {
                        onSuccess: (res) => res.password && onPassword(res.user, res.password),
                        onError: (e: Error) => setError(e.message),
                      },
                    );
                  }}
                >
                  Réinitialiser
                </Button>
              </div>
            </>
          ) : (
            <div className="hint">
              Compte {user.source} : son mot de passe est géré par son système d'origine.
            </div>
          )}
        </section>

        <section className="modal-section">
          <div className="form-label">Accès</div>
          <div className="hint">
            {user.disabled
              ? 'Ce compte est désactivé : il ne peut plus se connecter, ni au back-office ni à la carte.'
              : 'Désactiver coupe la connexion sans supprimer le compte ni son historique.'}
          </div>
          <div className="modal-actions tight">
            <Button
              variant={user.disabled ? 'primary' : 'alert'}
              icon={user.disabled ? 'check' : 'x'}
              loading={status.isPending}
              disabled={isSelf && !user.disabled}
              title={isSelf && !user.disabled ? 'On ne désactive pas son propre compte' : undefined}
              onClick={() => {
                setError(null);
                status.mutate(
                  { id: user.id, disabled: !user.disabled },
                  { onSuccess: onClose, onError: (e: Error) => setError(e.message) },
                );
              }}
            >
              {user.disabled ? 'Réactiver le compte' : 'Désactiver le compte'}
            </Button>
          </div>
        </section>

        {error && <div className="err-msg">{error}</div>}
      </div>
    </Modal>
  );
}

/** Mot de passe affiché une seule fois, avec copie — même contrat que les clés d'API. */
function PasswordOnceModal({
  user,
  password,
  onClose,
}: {
  user: User;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal opened onClose={onClose} title="Mot de passe du compte" size={480} centered>
      <div className="modal-form">
        <div className="err-msg">
          <Icon name="shieldcheck" /> Ce mot de passe n'est affiché qu'une fois. Transmettez-le à{' '}
          <strong>{user.email || user.externalId}</strong> par un canal sûr — il ne peut plus être
          relu, seulement remplacé.
        </div>
        <div className="keybox">
          <code>{password}</code>
          <Button
            size="sm"
            icon={copied ? 'check' : 'copy'}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(password);
                setCopied(true);
              } catch {
                /* presse-papiers indisponible : l'utilisateur copie à la main */
              }
            }}
          >
            {copied ? 'Copié' : 'Copier'}
          </Button>
        </div>
        <div className="modal-actions">
          <Button variant="primary" onClick={onClose}>
            J'ai noté le mot de passe
          </Button>
        </div>
      </div>
    </Modal>
  );
}
